import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AxiosError } from 'axios';

import type { TaskResponse } from '../../client';
import { useAppNavigate, useTask, useTeam, useDocumentTitle } from '../../hooks';

import { QueryResult } from '../molecules';
import { CompletedTaskReaction, Page, TaskView } from '../organisms';

export function TaskPage() {
  const { taskId } = useParams();
  const navigate = useAppNavigate();
  const [taskCompleted, setTaskCompleted] = useState<boolean>(false);
  const team = useTeam();

  if (taskId === undefined) {
    throw new Error('taskId must be an integer');
  }

  const task = useTask(taskId);
  const { mutate: mutateTask } = task;

  // Update browser tab title with team name and task ID
  useDocumentTitle(`${team.name} / ${taskId}`);

  const handleTaskProgress = useCallback(
    async (_taskData: TaskResponse, progress: number) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          mutateTask({ requestBody: { effort: progress } });
          resolve();
        }, 0);
      });
    },
    [mutateTask],
  );

  const handleTaskStarted = useCallback(
    async (_taskData: TaskResponse) => {
      // this is a hack: adding a 0 effort to the task will not progress it in any way, but will mark it as started,
      // which is exactly what is needed here
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          mutateTask({ requestBody: { effort: 0 } });
          resolve();
        }, 0);
      });
    },
    [mutateTask],
  );

  const handleTaskCompleted = useCallback(
    async (taskData: TaskResponse) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          mutateTask({ requestBody: { effort: taskData.total_effort } });
          setTaskCompleted(true);
          resolve();
        }, 0);
      });
    },
    [mutateTask],
  );

  const { error } = task;
  useEffect(() => {
    if (error instanceof AxiosError && error.response?.status === 404) {
      console.warn(`Task ${taskId} could not be found. Navigating user back home.`);
      navigate.toHome();
    }
  }, [error, navigate, taskId]);

  return (
    <Page title={'Task details'}>
      <QueryResult
        query={task}
        renderAs={(data) => (
          <TaskView
            task={data}
            onTaskProgress={handleTaskProgress}
            onTaskStarted={handleTaskStarted}
            onTaskCompleted={handleTaskCompleted}
          />
        )}
      />
      <CompletedTaskReaction active={taskCompleted} />
    </Page>
  );
}
